import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import './DownloadPage.css';
import { LandingI18nProvider, langByCode, useT } from './i18n';
import ICON_PATHS from './platformIcons';
import Logo from '../assets/logo.png';

/**
 * Stránka na stiahnutie appiek — desktop (mac/win/linux) + mobil (stores).
 *
 * DVE VECI, KTORÉ SA NESMÚ STRATIŤ:
 *
 * 1. Odkaz, verzia AJ veľkosť sa čítajú ŽIVO z update feedu (latest*.yml).
 *    Ručne písaná verzia by tu raz ostala visieť stará a nikto by si to
 *    nevšimol — presne preto to tak robí aj Signal.
 * 2. Systém sa určuje PRED vykreslením, inline skriptom v `public/index.html`,
 *    ktorý zapíše `data-os` na <html>. Do stránky ide všetkých päť tlačidiel
 *    a CSS ukáže to jediné, ktoré k systému patrí. Preto nič nepreblikne ani
 *    v okamihu, keď React ešte nebeží.
 *
 * Na TELEFÓNE je hlavné tlačidlo OBCHOD, nie desktopový balík — kto otvorí
 * odkaz v mobile, inak dostane tlačidlo, ktoré mu je na nič.
 */

// `.com` je kanonická doména appky. `.sk` ten istý feed servíruje tiež a MUSÍ
// tak ostať — nainštalované desktopy ho majú zapečený v app-update.yml a nedá
// sa im zmeniť. Ale nový kód naň už závisieť nemá.
const FEED_BASE = 'https://my.effzone.com/desktop-updates';
const APP_STORE_URL = 'https://apps.apple.com/sk/app/effzone/id6747626666';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=scan.sbs.app';

const DESKTOP_KEYS = ['mac', 'windows', 'linux'];
const MOBILE_KEYS = ['ios', 'android'];
const ALL_KEYS = [...DESKTOP_KEYS, ...MOBILE_KEYS];

const DESKTOP_FEEDS = {
  mac: 'latest-mac.yml',
  windows: 'latest.yml',
  linux: 'latest-linux.yml',
};

/** Ikona ku každej podobe. macOS aj App Store nesú to isté jablko. */
const ICON_BY_KEY = {
  mac: 'apple',
  windows: 'windows',
  linux: 'linux',
  ios: 'apple',
  android: 'android',
};

/** Miniparser latest*.yml — potrebujeme len version, path a size. */
function parseFeedYml(text) {
  const version = text.match(/^version:\s*(.+)\s*$/m)?.[1] ?? null;
  // `path` je artefakt pre updater; mac ZIP je preň, ale človek sťahuje DMG —
  // preto medzi `files` prednostne hľadáme dmg.
  const files = [...text.matchAll(/url:\s*(.+?)\s*$/gm)].map((m) => m[1]);
  const sizes = [...text.matchAll(/size:\s*(\d+)\s*$/gm)].map((m) => Number(m[1]));
  const fallbackPath = text.match(/^path:\s*(.+)\s*$/m)?.[1] ?? files[0] ?? null;
  const dmgIndex = files.findIndex((f) => f.endsWith('.dmg'));
  const fileName = dmgIndex >= 0 ? files[dmgIndex] : fallbackPath;
  const fileIndex = files.indexOf(fileName);
  const size = fileIndex >= 0 ? sizes[fileIndex] ?? null : sizes[0] ?? null;
  return fileName ? { version, fileName, size } : null;
}

const formatSize = (bytes) =>
  typeof bytes === 'number' && bytes > 0 ? `${Math.round(bytes / 1024 / 1024)} MB` : null;

/** Systém, ktorý pred vykreslením zapísal inline skript v index.html. */
const readOs = () => {
  if (typeof document === 'undefined') return 'mac';
  const value = document.documentElement.getAttribute('data-os');
  return ALL_KEYS.includes(value) ? value : 'mac';
};

const PlatformIcon = ({ name, className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
    <path d={ICON_PATHS[name]} />
  </svg>
);

const Chevron = () => (
  <svg
    className="dlp__arrow"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/** Štylizovaná maketa okna. ZÁMERNE nie je to skutočná snímka — na verejnej
 *  stránke by ukázala mená a správy zo živého účtu. */
const WindowMockup = () => {
  /*
    Poloha myši ZÁMERNE nie je v stave Reactu.
    Bola — a stálo to prekreslenie celého podstromu pri každom pohybe myši
    (namerané: 45 zápisov atribútov na jeden ťah, každá hodnota dvakrát).
    Naklonenie je čistá prezentácia, nič z neho nevstupuje do vykreslenia,
    tak sa píše rovno na uzol cez ref. React tu počas hoveru nemá čo robiť.

    Druhá polovica opravy je v CSS: úvodné nabehnutie beží na OBALE
    (`dlp__win-in`) a naklonenie na okne (`dlp__win`). Kým to bolo na jednom
    prvku, hover prepínal `animation: none` a späť — teda pri každom príchode
    a odchode myši sa úvodná animácia zahadzovala a nasadzovala nanovo a okno
    skočilo, lebo prechod bol vypísaný len v tej hoverovej vetve.
  */
  const winRef = React.useRef(null);
  const frame = React.useRef(0);
  const nehybne = React.useRef(false);

  useEffect(() => {
    // Kto si vypol pohyb, ten ho nedostane. Otázku „je tu naozaj myš?"
    // si TU ZÁMERNE neklademe — o tom rozhoduje jediné miesto, a to je
    // `@media (hover: hover) and (pointer: fine)` v CSS. Keby to skúmali
    // obaja, stačilo by jedno prostredie s inou odpoveďou a efekt by ticho
    // zmizol, hoci by druhá polovica fungovala.
    nehybne.current = typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return () => cancelAnimationFrame(frame.current);
  }, []);

  const zapis = useCallback((x, y) => {
    const el = winRef.current;
    if (!el) return;
    el.style.setProperty('--mx', x.toFixed(3));
    el.style.setProperty('--my', y.toFixed(3));
  }, []);

  // mousemove chodí aj 120-krát za sekundu; do štýlu sa píše najviac raz za
  // snímku, aby z toho nebolo toľko isto prepočtov rozloženia.
  const onMove = useCallback((event) => {
    if (nehybne.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = ((event.clientY - rect.top) / rect.height) * 2 - 1;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => zapis(x, y));
  }, [zapis]);

  const onLeave = useCallback(() => {
    cancelAnimationFrame(frame.current);
    zapis(0, 0);
  }, [zapis]);

  const rows = [
    { color: '#1e7f6b', top: '74%', bottom: '52%' },
    { color: '#c04b2d', top: '62%', bottom: '70%' },
    { color: '#6c60da', top: '80%', bottom: '44%' },
    { color: '#a36126', top: '56%', bottom: '64%' },
  ];

  return (
    <div className="dlp__shot dlp__rise dlp__d5" onMouseMove={onMove} onMouseLeave={onLeave}>
      {/* Obal nesie úvodné nabehnutie, okno vnútri naklonenie za myšou.
          Dve animácie na dvoch prvkoch, takže si nemajú ako liezť do cesty. */}
      <div className="dlp__win-in" aria-hidden="true">
        <div className="dlp__win" ref={winRef}>
          <span className="dlp__glare" />
          <div className="dlp__bar">
            <i className="dlp__dot" style={{ background: '#ff5f57' }} />
            <i className="dlp__dot" style={{ background: '#febc2e' }} />
            <i className="dlp__dot" style={{ background: '#28c840' }} />
          </div>
          <div className="dlp__winbody">
            <div className="dlp__rail">
              <i className="dlp__rail-on" />
              <i />
              <i />
              <i />
            </div>
            <div className="dlp__list">
              {rows.map((row) => (
                <div className="dlp__row" key={row.color}>
                  <span className="dlp__av" style={{ background: row.color }} />
                  <span style={{ flex: 1 }}>
                    <span className="dlp__ln" style={{ width: row.top }} />
                    <span
                      className="dlp__ln"
                      style={{ width: row.bottom, marginTop: 5, opacity: 0.6 }}
                    />
                  </span>
                </div>
              ))}
            </div>
            <div className="dlp__chat">
              <div className="dlp__bub">
                <span className="dlp__ln" style={{ width: 120 }} />
                <span className="dlp__ln" style={{ width: 78, marginTop: 6 }} />
              </div>
              <div className="dlp__bub dlp__bub--me">
                <span className="dlp__ln" style={{ width: 96 }} />
              </div>
              <div className="dlp__bub">
                <span className="dlp__ln" style={{ width: 140 }} />
              </div>
              <div className="dlp__bub dlp__bub--me">
                <span className="dlp__ln" style={{ width: 64 }} />
                <span className="dlp__ln" style={{ width: 104, marginTop: 6 }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DownloadPageInner = () => {
  const t = useT();
  const [feeds, setFeeds] = useState(null); // null = načítava; {} = zlyhalo
  // Zdrojom pravdy ostáva `data-os` na <html> (zapísal ho inline skript);
  // React ho len číta a pri kliknutí na inú platformu prepíše, aby sa
  // podoba tlačidla riadila jedným miestom, nie dvomi.
  const [os, setOs] = useState(readOs);

  const chooseOs = useCallback((next) => {
    document.documentElement.setAttribute('data-os', next);
    setOs(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      Object.entries(DESKTOP_FEEDS).map(async ([key, file]) => {
        try {
          const res = await fetch(`${FEED_BASE}/${file}`, { cache: 'no-store' });
          if (!res.ok) return [key, null];
          return [key, parseFeedYml(await res.text())];
        } catch {
          return [key, null];
        }
      }),
    ).then((entries) => {
      if (!cancelled) setFeeds(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const entryFor = useCallback(
    (key) => {
      if (key === 'ios' || key === 'android') {
        return {
          key,
          name: t(key === 'ios' ? 'download.ios' : 'download.android'),
          ctaLabel: t(key === 'ios' ? 'download.ctaAppStore' : 'download.ctaGooglePlay'),
          href: key === 'ios' ? APP_STORE_URL : PLAY_STORE_URL,
          meta: t(key === 'ios' ? 'download.appStore' : 'download.googlePlay'),
          ctaMeta: t(key === 'ios' ? 'download.iosDevices' : 'download.android'),
        };
      }
      const feed = feeds?.[key];
      if (!feed) return null;
      // Poznámka k macu sa ČÍTA Z NÁZVU SÚBORU: universal balík sám povie, že
      // je pre oba procesory, a keby sa niekedy vrátil arm64, sám sa opraví.
      const macNote = key !== 'mac'
        ? null
        : /arm64/i.test(feed.fileName)
          ? t('download.appleSilicon')
          : t('download.universal');
      const meta = [feed.version ? `v${feed.version}` : null, formatSize(feed.size)]
        .filter(Boolean)
        .join(' · ');
      return {
        key,
        name: t(`download.${key}`),
        ctaLabel: `${t('download.downloadFor')} ${t(`download.${key}`)}`,
        href: `${FEED_BASE}/${encodeURIComponent(feed.fileName)}`,
        meta,
        ctaMeta: [meta, macNote, key === 'linux' ? 'AppImage' : null].filter(Boolean).join(' · '),
      };
    },
    [feeds, t],
  );

  const entries = useMemo(
    () => Object.fromEntries(ALL_KEYS.map((key) => [key, entryFor(key)])),
    [entryFor],
  );

  const feedsFailed = feeds != null && !feeds.mac && !feeds.windows && !feeds.linux;
  const otherDesktops = DESKTOP_KEYS.filter((key) => key !== os);

  return (
    <div className="dlp">
      <div className="dlp__aurora" aria-hidden="true">
        <i className="dlp__blob dlp__blob--1" />
        <i className="dlp__blob dlp__blob--2" />
        <i className="dlp__blob dlp__blob--3" />
        <i className="dlp__blob dlp__blob--4" />
      </div>
      <div className="dlp__veil" aria-hidden="true" />

      <div className="dlp__page">
        <Link to="/" className="dlp__brand dlp__rise dlp__d1">
          <img src={Logo} alt="" />
          <span>Effzone</span>
        </Link>

        <div className="dlp__hero">
          <h1 className="dlp__title dlp__rise dlp__d2">{t('download.title')}</h1>
          <p className="dlp__lede dlp__rise dlp__d3">{t('download.subtitle')}</p>

          {/* Všetkých päť tlačidiel je v stránke; ukáže sa to jediné, ktoré
              patrí k `data-os` na <html>. */}
          {ALL_KEYS.map((key) => {
            const entry = entries[key];
            if (!entry) return null;
            return (
              <a
                key={key}
                className={`dlp__cta dlp__cta--${key}`}
                href={entry.href}
              >
                <PlatformIcon name={ICON_BY_KEY[key]} />
                <span className="dlp__cta-txt">
                  <span className="dlp__cta-main">{entry.ctaLabel}</span>
                  {entry.ctaMeta && <span className="dlp__cta-meta">{entry.ctaMeta}</span>}
                </span>
                <span className="dlp__cta-dl">
                  <Download size={20} strokeWidth={2.4} aria-hidden />
                </span>
              </a>
            );
          })}

          {feeds == null && <p className="dlp__loading">{t('download.loading')}</p>}

          <div className="dlp__alt dlp__rise dlp__d4">
            <span>
              {t(MOBILE_KEYS.includes(os) ? 'download.altMobile' : 'download.altDesktop')}{' '}
            </span>
            {otherDesktops.map((key, index) => (
              <React.Fragment key={key}>
                {index > 0 && <span> · </span>}
                <button type="button" onClick={() => chooseOs(key)}>
                  {t(`download.${key}`)}
                </button>
              </React.Fragment>
            ))}
          </div>

          <WindowMockup />
        </div>

        <div className="dlp__sec dlp__rise dlp__d5">
          <p className="dlp__sec-h">{t('download.sectionDesktop')}</p>
          <div className="dlp__grid">
            {DESKTOP_KEYS.map((key) => {
              const entry = entries[key];
              if (!entry) return null;
              return (
                <a className="dlp__card" href={entry.href} key={key}>
                  {key === os && <span className="dlp__tag">{t('download.yourSystem')}</span>}
                  <span className="dlp__ic">
                    <PlatformIcon name={ICON_BY_KEY[key]} />
                  </span>
                  <span>
                    <span className="dlp__c-name">{entry.name}</span>
                    <span className="dlp__c-meta">{entry.meta}</span>
                  </span>
                  <Chevron />
                </a>
              );
            })}
          </div>
          {/* Platí LEN pre Windows — mac je podpísaný a notarizovaný. */}
          <p className="dlp__note">{t('download.smartscreen')}</p>
        </div>

        {feedsFailed && (
          <p className="dlp__error">
            {t('download.feedError')}{' '}
            <button type="button" className="dlp__retry" onClick={() => window.location.reload()}>
              {t('download.retry')}
            </button>
          </p>
        )}

        <div className="dlp__facts">
          {['notarized', 'selfUpdate', 'sameAccount'].map((key) => (
            <div className="dlp__fact" key={key}>
              <b>{t(`download.fact.${key}.title`)}</b>
              <span>{t(`download.fact.${key}.text`)}</span>
            </div>
          ))}
        </div>

        <div className="dlp__sec dlp__rise dlp__d6">
          <p className="dlp__sec-h">{t('download.sectionMobile')}</p>
          <div className="dlp__grid dlp__grid--two">
            {MOBILE_KEYS.map((key) => {
              const entry = entries[key];
              return (
                <a className="dlp__card" href={entry.href} key={key}>
                  {key === os && <span className="dlp__tag">{t('download.yourSystem')}</span>}
                  <span className="dlp__ic">
                    <PlatformIcon name={ICON_BY_KEY[key]} />
                  </span>
                  <span>
                    <span className="dlp__c-name">
                      {t(key === 'ios' ? 'download.iosDevices' : 'download.android')}
                    </span>
                    <span className="dlp__c-meta">{entry.meta}</span>
                  </span>
                  <Chevron />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const DownloadPage = ({ lang = 'SK' }) => {
  useEffect(() => {
    document.documentElement.lang = langByCode(lang).htmlLang;
    window.scrollTo(0, 0);
  }, [lang]);
  return (
    <LandingI18nProvider lang={lang}>
      <DownloadPageInner />
    </LandingI18nProvider>
  );
};

export default DownloadPage;
